import { PushNotifications } from '@capacitor/push-notifications';
import { AndroidSettings, NativeSettings } from 'capacitor-native-settings';
import { initializeApp } from "firebase/app";
import { getMessaging, getToken, Messaging } from "firebase/messaging";

import api from '../api/apiClient';
import { endpoints } from '../api/endpoints';
import { getPlatform } from '../utils';
import config from '../config';

const firebaseConfig = {
    apiKey: config.webPushNoti.apiKey,
    authDomain: config.webPushNoti.authDomain,
    projectId: config.webPushNoti.projectId,
    storageBucket: config.webPushNoti.storageBucket,
    messagingSenderId: config.webPushNoti.messagingSenderId,
    appId: config.webPushNoti.appId
};


export class PushNotiService {
    private pushNotiService: NativePushNotiService | WebPushNotiService | null = null;

    constructor(eventCb?: (event: string, data: any) => void) {
        this.pushNotiService = getPlatform() === "web"
        ? new WebPushNotiService()
        : new NativePushNotiService(eventCb);
    }

    async init() {
        if (this.pushNotiService) {
            await this.pushNotiService.init();
        }
    }
}


class NativePushNotiService {
    
    private pushNotiPermissionStatus: string | null = null;

    private static instance: NativePushNotiService;

    private eventCb?: (event: string, data: any) => void;

    constructor(eventCb?: (event: string, data: any) => void) { 
        if (NativePushNotiService.instance) {
            return NativePushNotiService.instance;
        }
        NativePushNotiService.instance = this;
        this.eventCb = eventCb;
 
        PushNotifications.addListener('registration', async (token) => {
            console.log('Registration token: ', token.value);
            this.emitCb('registration', { token: token.value });

            const notiToken = token.value;
            const localToken = localStorage.getItem('notiToken');
            if(!localToken || localToken !== notiToken) {
                localStorage.setItem("notiToken", notiToken);
                await this.sendNotiToken(notiToken);
                this.subscribeToTopic("announcement");
            }
        });
    
        PushNotifications.addListener('registrationError', err => {
            console.log('Registration error: ', err.error);
            this.emitCb('registration', { error: err.error });
        });
    
        PushNotifications.addListener('pushNotificationReceived', notification => {
            console.log('Push notification received: ', notification);
            this.emitCb('pushNotificationReceived', notification);
        });
    
        PushNotifications.addListener('pushNotificationActionPerformed', notification => {
            this.emitCb('pushNotificationActionPerformed', notification);
        });
    }   

    async init(){ // why don't write the logic in constructor, cause we can call (init) to request again. if the logic is in constructor, since it's singledon, it only calls once!!!

        let permStatus = await PushNotifications.checkPermissions();

        if (permStatus.receive === 'prompt' || permStatus.receive === 'prompt-with-rationale') {
            permStatus = await PushNotifications.requestPermissions();
        }
        this.pushNotiPermissionStatus = permStatus.receive;

        if (permStatus.receive !== 'granted') return;

        PushNotifications.register();        
    }

    private emitCb(event: string, data: any) {
        if (this.eventCb) {
            this.eventCb(event, data);
        }
    }

    createDefaultChannels() {
        PushNotifications.createChannel({
            id : '1',
            name: 'vibrate',
            visibility: 1,
            vibration: true
        });
        PushNotifications.createChannel({
            id : '2',
            name: 'not vibrate',
            visibility: 0,
            vibration: false
        });
    }

    removeAllNotiListener () {
        PushNotifications.removeAllListeners();
    }

    getNotiPermStatus (){
        return this.pushNotiPermissionStatus;
    }

    goToNotiSetting () {
        NativeSettings.openAndroid({
            option: AndroidSettings.AppNotification,
        });
    }

    async sendNotiToken (notiToken: string) {
        let param = {
            notiToken,
            notiType: 'android'
		};
        const result : any = await api.put(endpoints.pushNoti.sendNotiToken, param)
        if(result.code !== "200") console.warn("send noti token failed: ", result.message)
    }

    async subscribeToTopic (topic: string) {
        let param = {
            topic
		};
        const result : any = await api.put(endpoints.pushNoti.subscribeToTopic, param)
        if(result.code !== "200") console.warn("subscribe to topic failed: ", result.message)
    }

    async unsubscribeToTopic (topic: string) {
        let param = {
            topic
		};
        const result : any = await api.put(endpoints.pushNoti.unsubscribeToTopic, param)
        if(result.code !== "200") console.warn("unsubscribe to topic failed: ", result.message)
    }
}


class WebPushNotiService {
    private messaging;

    private static instance: WebPushNotiService;
  
    constructor() {
      if (WebPushNotiService.instance) {
        return WebPushNotiService.instance;
      }
      WebPushNotiService.instance = this;

      const app = initializeApp(firebaseConfig);
      this.messaging = getMessaging(app);
    }
  
    async init() {
      console.log("Initializing Web Push Notifications...");

    //   await new Promise(resolve => setTimeout(resolve, 5000))
  
      const permission = await Notification.requestPermission();
      if (permission !== "granted") {
        console.log("Web Push Notification permission denied.");
        return;
      }
  
      const notiToken = await getToken(this.messaging as Messaging);
      const localToken = localStorage.getItem('notiToken');
      console.log("FCM Web Token:", notiToken);
      if(!localToken || localToken !== notiToken) {
        localStorage.setItem("notiToken", notiToken);
        await this.sendNotiToken(notiToken);
        this.subscribeToTopic("announcement");
      }
  
    }
  
    private async sendNotiToken(notiToken: string) {
        let param = {
            notiToken,
            notiType: 'web'
		};
        const result : any = await api.put(endpoints.pushNoti.sendNotiToken, param)
        if(result.code !== "200") console.warn("send noti token failed: ", result.message)
    }

    async subscribeToTopic (topic: string) {
        let param = {
            topic
		};
        const result : any = await api.put(endpoints.pushNoti.subscribeToTopic, param)
        if(result.code !== "200") console.warn("subscribe to topic failed: ", result.message)
    }

    async unsubscribeToTopic (topic: string) {
        let param = {
            topic
		};
        const result : any = await api.put(endpoints.pushNoti.unsubscribeToTopic, param)
        if(result.code !== "200") console.warn("unsubscribe to topic failed: ", result.message)
    }
}